import React, { useState } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { HelpCircle, ChevronDown, ChevronUp, Package, CreditCard, Truck, Wrench, FileText, Phone } from 'lucide-react';
import SEO from '../components/SEO';

const FAQ = () => {
  const [openItems, setOpenItems] = useState({});

  const toggleItem = (categoryIndex, itemIndex) => {
    const key = `${categoryIndex}-${itemIndex}`;
    setOpenItems(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const faqCategories = [
    {
      icon: <Package className="h-6 w-6 text-[#7bb5d4]" />,
      title: 'Products & Equipment',
      faqs: [
        {
          question: 'What types of medical equipment do you offer?',
          answer: 'We offer a comprehensive range of durable medical equipment including mobility aids (wheelchairs, walkers, rollators), bath safety equipment (shower chairs, grab bars, transfer benches), hospital beds, respiratory equipment, incontinence supplies, and daily living aids. Browse our full catalog to see all available products.'
        },
        {
          question: 'How do I know which equipment is right for me?',
          answer: 'Our experienced staff can help you determine the best equipment for your needs. We recommend consulting with your healthcare provider first, then contacting us for a personalized consultation. We consider factors like your mobility level, living situation, and specific health requirements.'
        },
        {
          question: 'Are your products new or refurbished?',
          answer: 'We primarily offer new equipment from trusted manufacturers. In some cases, we may have certified pre-owned equipment available at reduced prices. All pre-owned equipment is thoroughly inspected, sanitized, and tested before sale. We clearly indicate whether a product is new or pre-owned.'
        },
        {
          question: 'Do your products come with warranties?',
          answer: 'Yes, most of our products come with manufacturer warranties. Warranty terms vary by product and manufacturer, typically ranging from 1 to 5 years. We also offer extended warranty options on select items. Contact us for specific warranty information on any product.'
        }
      ]
    },
    {
      icon: <CreditCard className="h-6 w-6 text-[#7bb5d4]" />,
      title: 'Insurance & Payment',
      faqs: [
        {
          question: 'Do you accept insurance?',
          answer: 'Yes, we work with Medicare, Medicaid, and most major private insurance providers. Coverage varies by plan and product type. Our team can help verify your benefits and explain your coverage options. We handle insurance claims and paperwork on your behalf.'
        },
        {
          question: 'What if my insurance doesn\'t cover a product?',
          answer: 'If your insurance doesn\'t cover a particular product or covers only a portion, you\'ll be responsible for the remaining balance. We offer competitive pricing and can discuss payment options. In some cases, we may be able to suggest alternative products that have better coverage.'
        },
        {
          question: 'Do you offer payment plans?',
          answer: 'Yes, we understand that medical equipment can be a significant investment. We offer flexible payment options for qualifying customers. Please contact us to discuss available payment arrangements based on your situation.'
        },
        {
          question: 'How do I get a price quote?',
          answer: 'You can request a quote through our website\'s contact form or by calling us directly. We\'ll need some information about the product you\'re interested in and your insurance details (if applicable) to provide an accurate quote. Quotes are provided free of charge with no obligation.'
        }
      ]
    },
    {
      icon: <Truck className="h-6 w-6 text-[#7bb5d4]" />,
      title: 'Delivery & Setup',
      faqs: [
        {
          question: 'Do you deliver equipment?',
          answer: 'Yes, we offer delivery services for most equipment. Delivery options and fees vary based on your location and the type of equipment. For larger items like hospital beds and power wheelchairs, we provide professional delivery and setup services.'
        },
        {
          question: 'How long does delivery take?',
          answer: 'Delivery times vary based on product availability and your location. In-stock items typically ship within 1-3 business days. Custom or special-order items may take 1-2 weeks. We\'ll provide an estimated delivery timeframe when you place your order.'
        },
        {
          question: 'Is setup included with delivery?',
          answer: 'Setup is included for larger equipment items like hospital beds, patient lifts, and certain mobility devices. Our trained technicians will ensure proper installation and demonstrate how to use the equipment safely. For smaller items, detailed instructions are included.'
        },
        {
          question: 'What areas do you serve?',
          answer: 'We serve customers throughout the region and can ship many products nationwide. Delivery and setup services may be limited to certain geographic areas. Contact us with your location to confirm service availability.'
        }
      ]
    },
    {
      icon: <Wrench className="h-6 w-6 text-[#7bb5d4]" />,
      title: 'Service & Support',
      faqs: [
        {
          question: 'Do you offer equipment repairs?',
          answer: 'Yes, we provide repair services for many types of medical equipment. Our trained technicians can diagnose and fix issues with wheelchairs, walkers, hospital beds, and other devices. Contact us to schedule a repair assessment.'
        },
        {
          question: 'What if my equipment needs maintenance?',
          answer: 'Regular maintenance helps extend the life of your equipment. We offer maintenance services and can provide guidance on proper care for your specific equipment. Some items may require periodic professional servicing to maintain warranty coverage.'
        },
        {
          question: 'How do I return or exchange a product?',
          answer: 'Returns are accepted within 30 days of delivery for unused items in original packaging. Some medical equipment may not be eligible for return due to health regulations. Exchanges can be arranged if the product doesn\'t meet your needs. Contact our customer service team to initiate a return or exchange.'
        },
        {
          question: 'How can I contact customer support?',
          answer: 'You can reach our customer support team by phone at (240) 355-8003 during business hours (Mon-Fri 8AM-6PM, Sat 9AM-4PM), by email at info@kaloramamedical.com, or through our website\'s contact form. We strive to respond to all inquiries within 24 hours.'
        }
      ]
    },
    {
      icon: <FileText className="h-6 w-6 text-[#7bb5d4]" />,
      title: 'Orders & Documentation',
      faqs: [
        {
          question: 'Do I need a prescription for medical equipment?',
          answer: 'Some medical equipment requires a prescription or doctor\'s order, especially for insurance coverage. Items like hospital beds, certain wheelchairs, and oxygen equipment typically require a prescription. Our team can advise you on documentation requirements for specific products.'
        },
        {
          question: 'How do I track my order?',
          answer: 'Once your order ships, you\'ll receive a confirmation email with tracking information. You can also contact our customer service team for order status updates. For local deliveries, we\'ll contact you to schedule a convenient delivery time.'
        },
        {
          question: 'Can I modify or cancel my order?',
          answer: 'Orders can typically be modified or cancelled before they ship. Once an order has shipped, it cannot be cancelled but may be eligible for return. Custom or special-order items may not be eligible for cancellation. Contact us as soon as possible if you need to make changes.'
        },
        {
          question: 'Do you provide documentation for insurance claims?',
          answer: 'Yes, we provide all necessary documentation for insurance claims, including detailed invoices, product specifications, and proof of medical necessity when applicable. We can also assist with prior authorization requests and appeals if needed.'
        }
      ]
    }
  ];

  return (
    <div className="min-h-screen" data-testid="faq-page">
      <SEO 
        title="Frequently Asked Questions | Kalorama Medical"
        description="Find answers to common questions about medical equipment, insurance coverage, delivery, and our services at Kalorama Medical."
        keywords="FAQ, frequently asked questions, medical equipment questions, insurance coverage, delivery information"
      />
      
      {/* Hero */}
      <div className="bg-gradient-to-br from-[#1e3a5f] to-[#2c5282] text-white py-16">
        <div className="container mx-auto px-4 text-center">
          <HelpCircle className="h-16 w-16 mx-auto mb-6 text-[#7bb5d4]" />
          <h1 className="text-4xl md:text-5xl font-bold mb-4">Frequently Asked Questions</h1>
          <p className="text-xl text-blue-100 max-w-2xl mx-auto">
            Find answers to common questions about our products and services.
          </p>
        </div>
      </div>

      {/* FAQ Content */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            {faqCategories.map((category, categoryIndex) => (
              <div key={categoryIndex} className="mb-10">
                {/* Category Header */}
                <div className="flex items-center mb-6">
                  <div className="w-12 h-12 bg-blue-50 rounded-full flex items-center justify-center mr-4">
                    {category.icon}
                  </div>
                  <h2 className="text-2xl font-bold text-[#1e3a5f]">{category.title}</h2>
                </div>

                {/* FAQ Items */}
                <div className="space-y-3">
                  {category.faqs.map((faq, itemIndex) => {
                    const isOpen = openItems[`${categoryIndex}-${itemIndex}`];
                    return (
                      <Card 
                        key={itemIndex} 
                        className={`border-2 transition-all duration-300 cursor-pointer ${
                          isOpen ? 'border-[#7bb5d4] shadow-md' : 'border-blue-100 hover:border-[#7bb5d4]'
                        }`}
                        onClick={() => toggleItem(categoryIndex, itemIndex)}
                      >
                        <CardContent className="p-0">
                          <div className="flex items-center justify-between p-5">
                            <h3 className={`font-semibold pr-4 ${isOpen ? 'text-[#1e3a5f]' : 'text-gray-700'}`}>
                              {faq.question}
                            </h3>
                            {isOpen ? (
                              <ChevronUp className="h-5 w-5 text-[#7bb5d4] flex-shrink-0" />
                            ) : (
                              <ChevronDown className="h-5 w-5 text-gray-400 flex-shrink-0" />
                            )}
                          </div>
                          {isOpen && (
                            <div className="px-5 pb-5 pt-0">
                              <div className="border-t border-gray-100 pt-4">
                                <p className="text-gray-600 leading-relaxed">{faq.answer}</p>
                              </div>
                            </div>
                          )}
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              </div>
            ))}

            {/* Still Have Questions */}
            <Card className="mt-12 bg-gradient-to-br from-[#1e3a5f] to-[#2c5282] text-white">
              <CardContent className="p-8 text-center">
                <Phone className="h-12 w-12 mx-auto mb-4 text-[#7bb5d4]" />
                <h3 className="text-2xl font-bold mb-3">Still Have Questions?</h3>
                <p className="text-blue-100 mb-6 max-w-lg mx-auto">
                  Can't find what you're looking for? Our friendly team is here to help with any questions you may have.
                </p>
                <Link to="/contact">
                  <Button 
                    size="lg" 
                    className="bg-white text-[#1e3a5f] hover:bg-blue-50 rounded-full px-8 transition-all duration-300 hover:scale-105"
                  >
                    Contact Us
                  </Button>
                </Link>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>
    </div>
  );
};

export default FAQ;
